import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowRight, Check, FilePen, KeyRound, LineChart, MapPinHouse, Server, Signature } from "lucide-react";
import { Reveal } from "@/components/flow/Reveal";
import { EYEBROW } from "@/pages/home/SiteChrome";
import { EASE } from "@/lib/motion";
import { t } from "@/lib/i18n";
import { tb } from "@/lib/brandify";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------------------
   How it works — "Six steps. One property record.", taken literally.
   One rail is the record. It draws in as the section enters; each step lands
   as the rail reaches it. Who acts is always visible: the owner on their phone
   (upper lane), you — or Listed, automatically — in your workspace (lower
   lane). The rail ends *inside* a real property record that fills in field by
   field, in time with the steps. Hover a step or a record row: both light up.
   Phones first: one vertical rail, each step followed by the record field it
   writes, and the finished record beneath.
--------------------------------------------------------------------------- */

export const STEPS = [
  { id: "address", icon: MapPinHouse, who: "owner" },
  { id: "estimate", icon: LineChart, who: "auto" },
  { id: "offer", icon: FilePen, who: "you" },
  { id: "sign", icon: Signature, who: "owner" },
  { id: "onboarding", icon: KeyRound, who: "owner" },
  { id: "live", icon: Server, who: "auto" },
];
const N = STEPS.length;
const THUMB = "/img/interior-living.webp";
const laneOf = (who) => (who === "owner" ? "owner" : "you");

/* Motion — the rail's full draw, and where along it (0..1) each step sits. */
const DRAW = 1.9;
const at = (i) => (i + 0.5) / N;
const railV = { hidden: { scaleX: 0 }, show: { scaleX: 1, transition: { duration: DRAW, ease: [0.22, 1, 0.36, 1] } } };
const railVy = { hidden: { scaleY: 0 }, show: { scaleY: 1, transition: { duration: DRAW, ease: [0.22, 1, 0.36, 1] } } };
const pipV = (i) => ({ hidden: { scale: 0, opacity: 0 }, show: { scale: 1, opacity: 1, transition: { delay: DRAW * at(i), duration: 0.45, ease: EASE } } });
const cardV = (i) => ({ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { delay: DRAW * at(i) + 0.05, duration: 0.55, ease: EASE } } });
const rowV = (i) => ({ hidden: { opacity: 0, x: -6 }, show: { opacity: 1, x: 0, transition: { delay: DRAW * at(i) + 0.18, duration: 0.5, ease: EASE } } });
const ghostV = (i) => ({ hidden: { opacity: 1 }, show: { opacity: 0, transition: { delay: DRAW * at(i) + 0.18, duration: 0.3 } } });
const endV = { hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0, transition: { delay: DRAW + 0.25, duration: 0.5, ease: EASE } } };

const WHO = { owner: "bg-[var(--accent-soft)] text-ink", you: "bg-ink text-white", auto: "bg-fill text-ink/70 border border-hairline" };
const PIP = { owner: "bg-lavender brand-tint", you: "bg-ink", auto: "bg-ink/35" };

const hotProps = (id, setHot) => ({ onMouseEnter: () => setHot(id), onMouseLeave: () => setHot(null), onFocus: () => setHot(id), onBlur: () => setHot(null) });

const WhoTag = ({ who, testId }) => <span className={cn("inline-flex h-5 shrink-0 items-center rounded-full px-2 text-[10.5px] font-medium tracking-[0.02em]", WHO[who])} data-testid={testId}>{t(`hiw.who.${who}`)}</span>;

/* ---------- one step (wide: a card in its lane) ---------- */
function StepCard({ step, i, hot, setHot, testId, reduced }) {
  const Icon = step.icon;
  const on = hot === step.id;
  return (
    <motion.div
      variants={cardV(i)}
      whileHover={reduced ? undefined : { y: -3 }}
      tabIndex={0}
      {...hotProps(step.id, setHot)}
      className={cn("group relative min-w-0 rounded-[16px] border bg-surface px-3 py-3 outline-none transition-[border-color,box-shadow] duration-300 ease-editorial will-change-transform focus-visible:shadow-ring", on ? "border-ink/30 shadow-float" : "border-hairline")}
      data-testid={`${testId}-${step.id}`}
      data-who={step.who}
      data-hot={on ? "true" : "false"}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10.5px] tabular-nums text-ink/40">0{i + 1}</span>
        <span className="font-mono text-[10.5px] tabular-nums text-ink/40 truncate">{t(`hiw.${step.id}.time`)}</span>
      </div>
      <div className="mt-2.5 flex items-center gap-2">
        <span className={cn("inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] transition-colors duration-300", on ? "bg-ink text-white" : "bg-fill text-ink/75")}><Icon className="h-4 w-4" strokeWidth={1.9} /></span>
        <WhoTag who={step.who} testId={`${testId}-${step.id}-who`} />
      </div>
      <h3 className="mt-2.5 font-heading font-bold text-[15px] tracking-[-0.02em] text-ink leading-[1.15] text-balance">{t(`hiw.${step.id}.t`)}</h3>
      <p className="mt-1.5 text-[12.5px] text-ink/60 leading-[1.5]">{tb(`hiw.${step.id}.p`)}</p>
    </motion.div>
  );
}

/* ---------- one step (phones: a row on the vertical rail, then the field it writes) ---------- */
function StepRow({ step, i, hot, setHot, testId }) {
  const Icon = step.icon;
  const on = hot === step.id;
  return (
    <motion.li variants={cardV(i)} {...hotProps(step.id, setHot)} className="relative flex items-start gap-3 py-3 sm:py-4" data-testid={`${testId}-${step.id}`} data-who={step.who} data-hot={on ? "true" : "false"}>
      <motion.span variants={pipV(i)} className="relative z-[2] mt-[7px] flex h-7 w-7 shrink-0 items-center justify-center" aria-hidden>
        <span className={cn("block h-3 w-3 rounded-full ring-4 ring-canvas", PIP[step.who])} />
      </motion.span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-[10.5px] tabular-nums text-ink/40">0{i + 1}</span>
          <span className="font-mono text-[10.5px] tabular-nums text-ink/40 truncate">{t(`hiw.${step.id}.time`)}</span>
          <span className="ml-auto"><WhoTag who={step.who} testId={`${testId}-${step.id}-who`} /></span>
        </div>
        <div className="mt-1.5 flex items-start gap-3">
          <span className="mt-px inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-fill text-ink/75"><Icon className="h-4 w-4" strokeWidth={1.9} /></span>
          <div className="min-w-0 flex-1">
            <h3 className="font-heading font-bold text-[16px] tracking-[-0.02em] text-ink leading-tight">{t(`hiw.${step.id}.t`)}</h3>
            <p className="mt-1 text-[13px] text-ink/60 leading-[1.5]">{tb(`hiw.${step.id}.p`)}</p>
            {/* what this step writes to the record — the "one record" idea, right where the step is */}
            <motion.p variants={rowV(i)} className="mt-2.5 inline-flex max-w-full items-start gap-1.5 rounded-[10px] bg-fill pl-1.5 pr-2.5 py-1 text-[11.5px] leading-[1.4] text-ink/75" data-testid={`${testId}-${step.id}-record`}>
              <span className="mt-[1px] flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-success text-white"><Check className="h-2 w-2" strokeWidth={3.5} /></span>
              <span className="min-w-0"><span className="text-ink/45">{t(`hiw.rec.${step.id}`)} · </span>{t(`hiw.rec.${step.id}_v`)}</span>
            </motion.p>
          </div>
        </div>
      </div>
    </motion.li>
  );
}

/* ---------- the record the rail flows into ---------- */
function RecordCard({ hot, setHot, className }) {
  return (
    <div className={cn("relative overflow-hidden rounded-[22px] bg-ink text-white grain grain-dark", className)} data-testid="how-record">
      <span aria-hidden className="pointer-events-none absolute -top-24 right-0 h-[260px] w-[260px] translate-x-1/3 rounded-full" style={{ background: "radial-gradient(closest-side, var(--accent-glow), transparent 72%)", filter: "blur(26px)", opacity: 0.55 }} />
      <div className="relative p-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-white/45">{t("hiw.rec.eyebrow")}</p>
          <motion.span variants={endV} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2 h-6 text-[11px] font-medium text-white" data-testid="how-record-badge"><span className="h-1.5 w-1.5 rounded-full bg-success pulse-dot" />{t("hiw.rec.badge")}</motion.span>
        </div>
        <div className="mt-3.5 flex items-center gap-3">
          <img src={THUMB} alt="" loading="lazy" decoding="async" className="h-11 w-11 shrink-0 rounded-[12px] object-cover" />
          <div className="min-w-0">
            <p className="font-heading font-bold text-[17px] tracking-[-0.02em] leading-tight truncate">{t("hiw.rec.title")}</p>
            <p className="mt-0.5 text-[11.5px] text-white/50 truncate">{t("hiw.rec.meta")}</p>
          </div>
        </div>
        <ol className="mt-4 border-t border-white/10">
          {STEPS.map((s, i) => {
            const on = hot === s.id;
            return (
              <li key={s.id} {...hotProps(s.id, setHot)} className={cn("relative -mx-2 flex items-center gap-3 rounded-[10px] px-2 py-[9px] border-b border-white/10 last:border-b-0 transition-colors duration-300", on && "bg-white/[0.07]")} data-testid={`how-record-row-${s.id}`} data-hot={on ? "true" : "false"}>
                <motion.span variants={rowV(i)} className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-success text-white"><Check className="h-2.5 w-2.5" strokeWidth={3} /></motion.span>
                <span className="min-w-0 flex-1">
                  <span className={cn("block text-[10.5px] uppercase tracking-[0.12em] transition-colors duration-300", on ? "text-white/80" : "text-white/40")}>{t(`hiw.rec.${s.id}`)}</span>
                  <span className="relative block h-5">
                    <motion.span variants={ghostV(i)} aria-hidden className="absolute left-0 top-[7px] h-1.5 w-24 rounded-full bg-white/10" />
                    <motion.span variants={rowV(i)} className="absolute inset-0 truncate text-[13px] text-white leading-5 tabular-nums">{t(`hiw.rec.${s.id}_v`)}</motion.span>
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
        <motion.p variants={endV} className="mt-4 text-[11.5px] text-white/45 leading-[1.5]" data-testid="how-record-foot">{t("hiw.rec.foot")}</motion.p>
      </div>
    </div>
  );
}

/* ---------- wide: lanes · rail · record ---------- */
function RailWide({ hot, setHot, testId, reduced }) {
  const row = (who) => (laneOf(who) === "owner" ? 2 : 4);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_264px] xl:grid-cols-[minmax(0,1fr)_280px] gap-x-7 items-stretch">
      <div className="relative grid" style={{ gridTemplateColumns: `repeat(${N}, minmax(0, 1fr))`, gridTemplateRows: "auto minmax(0,1fr) 64px minmax(0,1fr) auto" }} data-testid={`${testId}-rail`}>
        <p className="pb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/40" style={{ gridColumn: "1 / -1", gridRow: 1 }}>{t("hiw.lane.owner")}</p>
        {STEPS.map((s, i) => (
          <div key={s.id} className={cn("relative flex min-w-0 flex-col px-1", laneOf(s.who) === "owner" ? "justify-end" : "justify-start")} style={{ gridColumn: i + 1, gridRow: row(s.who) }}>
            {laneOf(s.who) === "you" && <motion.span variants={cardV(i)} aria-hidden className="mx-auto h-5 w-px bg-hairline" />}
            <StepCard step={s} i={i} hot={hot} setHot={setHot} testId={testId} reduced={reduced} />
            {laneOf(s.who) === "owner" && <motion.span variants={cardV(i)} aria-hidden className="mx-auto h-5 w-px bg-hairline" />}
          </div>
        ))}
        {/* the rail — runs on into the record */}
        <div className="relative" style={{ gridColumn: "1 / -1", gridRow: 3 }}>
          <span aria-hidden className="absolute left-0 -right-7 top-1/2 -translate-y-1/2 h-px bg-hairline" />
          <motion.span aria-hidden variants={railV} className="absolute left-0 -right-7 top-1/2 -translate-y-1/2 h-[3px] origin-left rounded-full bg-ink/80 will-change-transform" />
          <motion.span variants={endV} aria-hidden className="absolute -right-7 top-1/2 -translate-y-1/2 translate-x-1/2 flex h-6 w-6 items-center justify-center rounded-full bg-ink text-white"><ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} /></motion.span>
          {STEPS.map((s, i) => {
            const on = hot === s.id;
            return (
              <motion.span key={s.id} variants={pipV(i)} className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ left: `${at(i) * 100}%` }} data-testid={`${testId}-pip-${s.id}`} data-hot={on ? "true" : "false"}>
                <span className={cn("block rounded-full ring-4 ring-canvas transition-[transform,box-shadow] duration-300 ease-editorial", PIP[s.who], on ? "h-4 w-4 shadow-[0_0_0_6px_rgba(21,19,15,0.08)]" : "h-3 w-3")} />
              </motion.span>
            );
          })}
        </div>
        <p className="pt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/40" style={{ gridColumn: "1 / -1", gridRow: 5 }}>{t("hiw.lane.you")}</p>
      </div>
      <RecordCard hot={hot} setHot={setHot} />
    </div>
  );
}

/* ---------- phones & tablets: the rail runs down the left; the record follows ---------- */
function RailStack({ hot, setHot, testId }) {
  return (
    <div className="sm:grid sm:grid-cols-[minmax(0,1fr)_280px] sm:gap-x-8 sm:items-start">
      <div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink/40" data-testid={`${testId}-lanes`}>
          {["owner", "you", "auto"].map((w) => (
            <span key={w} className="inline-flex items-center gap-1.5"><span className={cn("h-2 w-2 rounded-full", PIP[w])} />{t(`hiw.legend.${w}`)}</span>
          ))}
        </div>
        <ol className="relative mt-2" data-testid={`${testId}-rail`}>
          <span aria-hidden className="absolute left-[13px] top-5 bottom-5 w-px bg-hairline" />
          <motion.span aria-hidden variants={railVy} className="absolute left-[12px] top-5 bottom-5 w-[3px] origin-top rounded-full bg-ink/80" />
          {STEPS.map((s, i) => <StepRow key={s.id} step={s} i={i} hot={hot} setHot={setHot} testId={testId} />)}
        </ol>
      </div>
      <RecordCard hot={hot} setHot={setHot} className="mt-6 sm:mt-0 sm:sticky sm:top-24" />
    </div>
  );
}

/** The six steps + the record they build. Shared with /waitlist. */
export function StepsRail({ testId = "how-step", className }) {
  const reduced = useReducedMotion();
  const [hot, setHot] = useState(null);
  return (
    <motion.div initial={reduced ? false : "hidden"} whileInView="show" viewport={{ once: true, amount: 0.2 }} className={className} data-testid={`${testId}s`} data-hot={hot || ""}>
      <div className="hidden lg:block"><RailWide hot={hot} setHot={setHot} testId={testId} reduced={!!reduced} /></div>
      <div className="lg:hidden"><RailStack hot={hot} setHot={setHot} testId={testId} /></div>
    </motion.div>
  );
}

export function HowItWorks({ container }) {
  return (
    <section id="how" className="relative mt-10 sm:mt-16 scroll-mt-16 overflow-x-clip" data-testid="how-section">
      <div className={cn(container, "py-12 sm:py-16 lg:py-20")}>
        <div className="grid lg:grid-cols-12 gap-5 lg:gap-10 items-end">
          <div className="lg:col-span-7">
            <Reveal as="p" className={EYEBROW}>{t("hiw.eyebrow")}</Reveal>
            <Reveal as="h2" delay={0.04} className="mt-4 font-display text-[32px] sm:text-[42px] lg:text-[48px] text-ink max-w-[18ch]" data-testid="how-title">{t("hiw.title")}</Reveal>
          </div>
          <div className="lg:col-span-5 lg:pb-1">
            <Reveal as="p" delay={0.08} className="text-[15px] sm:text-[16px] text-ink/60 leading-[1.6] max-w-[48ch]">{tb("hiw.sub")}</Reveal>
            <Reveal delay={0.12} className="mt-3">
              <a href="#story" className="inline-flex min-h-[44px] items-center gap-1.5 text-[14px] font-medium text-ink hover:text-lavender transition-colors" data-testid="how-story-link">
                {t("hiw.link")} <ArrowDown className="h-3.5 w-3.5" />
              </a>
            </Reveal>
          </div>
        </div>
        <StepsRail className="mt-8 sm:mt-14" />
      </div>
    </section>
  );
}
